import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Bell, Clock, Calendar, ArrowRight, ExternalLink, LayoutGrid, List, Filter, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PageTemplate from '../components/Common/PageTemplate';
import duyurularData from '../data/Duyurular.json';

const AnnouncementCard = ({ announcement, index, viewMode, t }) => {
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                // Handle 'DD.MM.YYYY HH:MM:SS' format
                const parts = dateString.split(' ')[0].split('.');
                if (parts.length === 3) {
                    const day = parseInt(parts[0]);
                    const month = parseInt(parts[1]) - 1; // Month is 0-indexed
                    const year = parseInt(parts[2]);
                    const timeParts = dateString.split(' ')[1] ? dateString.split(' ')[1].split(':') : [0, 0, 0];
                    const hours = parseInt(timeParts[0]);
                    const minutes = parseInt(timeParts[1]);
                    const seconds = parseInt(timeParts[2]);
                    const newDate = new Date(year, month, day, hours, minutes, seconds);
                    return newDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
                }
                return dateString; // Return original if parsing fails
            }
            return date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
        } catch (e) {
            console.error("Date parsing error:", e);
            return dateString; // Fallback to original string on error
        }
    };

    const truncateTitle = (title, maxLength) => {
        if (title.length > maxLength) {
            return title.substring(0, maxLength) + '...';
        }
        return title;
    };

    const getCategoryColor = (category) => {
        switch(category) {
            case 'personel': return 'bg-blue-100 text-blue-800';
            case 'ogrenci': return 'bg-green-100 text-green-800';
            case 'etkinlik': return 'bg-purple-100 text-purple-800';
            case 'genel': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const cardContent = (
        <>
            <div className={viewMode === 'list' ? 'flex-grow' : ''}>
                <div className={`flex justify-between items-center mb-3 ${viewMode === 'list' ? 'flex-col items-start mb-2' : ''}`}>
                    <span className="text-sm text-gray-500 flex items-center font-medium">
                        <Calendar size={16} className="mr-2 text-blue-500" />
                        {formatDate(announcement.date)}
                    </span>
                    <div className={`flex gap-2 ${viewMode === 'list' ? 'mt-2 md:mt-0' : ''}`}>
                        {announcement.isNew && (
                            <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-500 text-white animate-pulse">
                                {t('pages.announcements.newBadge')}
                            </span>
                        )}
                        {announcement.category && (
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(announcement.category)}`}>
                                {t(`pages.announcements.categories.${announcement.category}`)}
                            </span>
                        )}
                    </div>
                </div>
                <h3 className={`text-2xl font-bold text-gray-800 mb-3 leading-tight ${viewMode === 'list' ? 'text-lg mb-1' : ''}`}>
                    {truncateTitle(announcement.title, 73)}
                </h3>
                {/* Açıklamalar kaldırıldı */}
            </div>
            <div className={`flex justify-between items-center text-blue-600 font-semibold group ${viewMode === 'list' ? 'flex-col items-end ml-4' : ''}`}>
                <span className={viewMode === 'list' ? 'hidden' : ''}>
                    {t('pages.announcements.viewDetails')}
                </span>
                {announcement.link && announcement.link.startsWith('http') ? (
                    <ExternalLink size={18} className="ml-2 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                ) : (
                    <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                )}
            </div>
        </>
    );

    const wrapperClass = `block h-full p-6 flex flex-col justify-between ${viewMode === 'list' ? 'w-full flex-row items-center' : ''}`;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.9 }}
            transition={{ duration: 0.5, type: 'spring' }}
            className={`bg-white rounded-2xl border border-gray-200 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1 ${viewMode === 'list' ? 'flex-row items-center p-4' : ''}`}
        >
            {announcement.link && announcement.link.startsWith('http') ? (
                <a
                    href={announcement.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={wrapperClass}
                >
                    {cardContent}
                </a>
            ) : announcement.link && announcement.link !== '#' ? (
                <Link
                    to={announcement.link}
                    className={wrapperClass}
                >
                    {cardContent}
                </Link>
            ) : (
                <div className={wrapperClass}>
                    {cardContent}
                </div>
            )}
        </motion.div>
    );
};

const Announcements = () => {
    const { t } = useTranslation();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('genel');
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [currentPage, setCurrentPage] = useState(1);
    const announcementsPerPage = 12; // Max 12 announcements per page

    const categories = useMemo(() => {
        const uniqueCategories = [...new Set(duyurularData.map(ann => ann.category))];
        return ['Tümü', ...uniqueCategories.filter(Boolean)]; // 'Boolean' removes undefined/null
    }, []);

    useEffect(() => {
        const loadAnnouncements = async () => {
            try {
                setLoading(true);
                await new Promise(res => setTimeout(res, 500)); // Simulate network delay
                setAnnouncements(duyurularData || []);
            } catch (error) {
                setError(t('pages.announcements.loadError'));
            } finally {
                setLoading(false);
            }
        };
        loadAnnouncements();
    }, []);

    const sortedAndFilteredAnnouncements = useMemo(() => {
        let filtered = announcements.filter(ann => {
            // Search only by title
            const matchesSearch = ann.title.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'Tümü' || ann.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });

        filtered.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (sortOrder === 'newest') {
                return dateB - dateA;
            } else {
                return dateA - dateB;
            }
        });
        return filtered;
    }, [announcements, searchTerm, selectedCategory, sortOrder]);

    // Pagination logic
    const indexOfLastAnnouncement = currentPage * announcementsPerPage;
    const indexOfFirstAnnouncement = indexOfLastAnnouncement - announcementsPerPage;
    const currentAnnouncements = sortedAndFilteredAnnouncements.slice(indexOfFirstAnnouncement, indexOfLastAnnouncement);

    const totalPages = Math.ceil(sortedAndFilteredAnnouncements.length / announcementsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Reset to first page when filters or search terms change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategory, sortOrder]);

    if (error) return <PageTemplate title={t('pages.announcements.error')}><div className="text-red-500 text-center py-10">{error}</div></PageTemplate>;

    return (
        <PageTemplate
            title={t('pages.announcements.pageTitle')}
            subtitle={t('pages.announcements.pageSubtitle')}
        >
            <div className="container mx-auto px-4 py-8 bg-white rounded-lg shadow-md my-8">
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 space-y-4 md:space-y-0">
                    <div className="relative w-full md:w-1/3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder={t('pages.announcements.searchPlaceholder')}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <select
                                className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-blue-500 transition-all duration-200"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                {categories.map(category => (
                                    <option key={category} value={category}>
                                        {category === 'Tümü' ? t('pages.announcements.allCategories') :
                                         t(`pages.announcements.categories.${category}`)}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                <ChevronDown size={16} />
                            </div>
                        </div>
                        <div className="relative">
                            <select
                                className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-blue-500 transition-all duration-200"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                            >
                                <option value="newest">{t('pages.announcements.sortNewest')}</option>
                                <option value="oldest">{t('pages.announcements.sortOldest')}</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                <ChevronDown size={16} />
                            </div>
                        </div>
                        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                            <button
                                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors duration-200`}
                                onClick={() => setViewMode('grid')}
                                title={t('pages.announcements.gridView')}
                            >
                                <LayoutGrid size={20} />
                            </button>
                            <button
                                className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors duration-200`}
                                onClick={() => setViewMode('list')}
                                title={t('pages.announcements.listView')}
                            >
                                <List size={20} />
                            </button>
                        </div>
                    </div>
                </div>
                
                <main>
                    <AnimatePresence mode="wait">
                        {loading ? (
                            <div className={`grid gap-6 ${viewMode === 'list' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                                {[...Array(viewMode === 'list' ? 3 : 6)].map((_, i) => (
                                    <motion.div 
                                        key={i} 
                                        className="bg-gray-100 rounded-xl shadow-md p-6 animate-pulse"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="h-5 bg-gray-300 rounded w-3/4 mb-4"></div>
                                        <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                                        <div className="h-4 bg-gray-300 rounded w-5/6 mb-4"></div>
                                        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                                    </motion.div>
                                ))}
                            </div>
                        ) : currentAnnouncements.length === 0 ? (
                            <motion.p
                                key="no-results"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="text-center text-gray-600 text-lg py-10"
                            >
                                {t('pages.announcements.noResults')}
                            </motion.p>
                        ) : (
                            <div className={`grid gap-6 ${viewMode === 'list' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                                {currentAnnouncements.map((ann, index) => (
                                    <AnnouncementCard key={ann.link || index} announcement={ann} index={index} viewMode={viewMode} t={t} />
                                ))}
                            </div>
                        )}
                    </AnimatePresence>
                </main>

                {totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-2 mt-8">
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {t('pages.announcements.previous')}
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i + 1}
                                onClick={() => paginate(i + 1)}
                                className={`px-4 py-2 border border-gray-300 rounded-lg ${currentPage === i + 1 ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'} transition-colors duration-200`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {t('pages.announcements.next')}
                        </button>
                    </div>
                )}
            </div>
        </PageTemplate>
    );
};

export default Announcements;
